// DataHistorySection - Component for viewing and reverting shared data backups
// Used in Settings when partners are linked

import React, { useState, useEffect } from 'react';
import { History, RotateCcw, Loader2, FileText, ShoppingBag, Heart, BookOpen, Clock, Check, Bell, CheckSquare } from 'lucide-react';
import { syncApi } from '../../services/apiService';

interface DataBackup {
    id: string;
    dataType: string;
    wasYours: boolean;
    resolvedAt: number;
}

const DATA_TYPE_INFO: Record<string, { label: string; icon: React.ElementType; color: string }> = {
    'birth-plan': { label: 'Birth Plan', icon: FileText, color: 'text-teal-600' },
    'hospital-bag': { label: 'Hospital Bag', icon: ShoppingBag, color: 'text-purple-600' },
    'name-shortlist': { label: 'Saved Names', icon: Heart, color: 'text-pink-600' },
    'saved-facts': { label: 'Saved Facts', icon: BookOpen, color: 'text-amber-600' },
    'reminders': { label: 'Reminders', icon: Bell, color: 'text-blue-600' },
    'checklists': { label: 'Checklists', icon: CheckSquare, color: 'text-green-600' },
};

function formatDate(timestamp: number): string {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
    });
}

function daysRemaining(resolvedAt: number): number {
    const thirtyDaysLater = (resolvedAt + 30 * 24 * 60 * 60) * 1000;
    const now = Date.now();
    return Math.max(0, Math.ceil((thirtyDaysLater - now) / (1000 * 60 * 60 * 24)));
}

export const DataHistorySection: React.FC = () => {
    const [backups, setBackups] = useState<DataBackup[]>([]);
    const [loading, setLoading] = useState(true);
    const [reverting, setReverting] = useState<string | null>(null);
    const [revertSuccess, setRevertSuccess] = useState<string | null>(null);

    useEffect(() => {
        loadBackups();
    }, []);

    const loadBackups = async () => {
        try {
            const data = await syncApi.getDataBackups();
            setBackups(data);
        } catch (error) {
            console.error('Failed to load backups:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRevert = async (dataType: string) => {
        setReverting(dataType);
        try {
            await syncApi.revertData(dataType);
            setRevertSuccess(dataType);
            // Reload backups to get updated state
            await loadBackups();
            setTimeout(() => setRevertSuccess(null), 3000);
        } catch (error) {
            console.error('Revert failed:', error);
            alert('Failed to revert. Please try again.');
        } finally {
            setReverting(null);
        }
    };

    if (loading) {
        return (
            <div className="bg-white/70 rounded-2xl p-4 flex items-center justify-center">
                <Loader2 size={20} className="animate-spin text-stone-400" />
            </div>
        );
    }

    if (backups.length === 0) {
        return null; // Don't show section if no backups
    }

    return (
        <div className="bg-white/70 rounded-2xl p-4 mt-4">
            <div className="flex items-center gap-2 mb-3">
                <History size={14} className="text-stone-500" />
                <h4 className="text-sm font-semibold text-stone-800">Data History</h4>
            </div>
            <p className="text-xs text-stone-500 mb-3">
                You can revert to the other version of shared data within 30 days.
            </p>

            <div className="space-y-2">
                {backups.map(backup => {
                    const info = DATA_TYPE_INFO[backup.dataType] || {
                        label: backup.dataType,
                        icon: FileText,
                        color: 'text-stone-600'
                    };
                    const Icon = info.icon;
                    const days = daysRemaining(backup.resolvedAt);
                    const isReverting = reverting === backup.dataType;
                    const justReverted = revertSuccess === backup.dataType;

                    return (
                        <div
                            key={backup.id}
                            className="flex items-center justify-between p-3 bg-stone-50 rounded-xl"
                        >
                            <div className="flex items-center gap-3">
                                <Icon size={16} className={info.color} />
                                <div>
                                    <p className="text-sm font-medium text-stone-800">{info.label}</p>
                                    <p className="text-xs text-stone-500">
                                        {backup.wasYours ? "Your version available" : "Partner's version available"}
                                        {' · '}
                                        <span className="text-stone-400">{days}d left</span>
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => handleRevert(backup.dataType)}
                                disabled={isReverting || justReverted}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all ${justReverted
                                        ? 'bg-emerald-100 text-emerald-700'
                                        : 'bg-white border border-stone-200 text-stone-600 hover:bg-stone-100'
                                    } disabled:opacity-50`}
                            >
                                {isReverting ? (
                                    <Loader2 size={12} className="animate-spin" />
                                ) : justReverted ? (
                                    <Check size={12} />
                                ) : (
                                    <RotateCcw size={12} />
                                )}
                                {justReverted ? 'Reverted!' : 'Revert'}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
